import React, { useEffect, useState } from 'react';
import { history } from 'umi';
import { useInterval, useRequest } from 'ahooks';
import { SortBy, SortableColumn } from 'tea-component/lib/table/addons/sortable';
import { PagingQuery, Table, TableColumn, Button, Bubble, notification, ConfigProvider } from 'tea-component';
import {
  getNumberPackageList,
  getNumberPackageStatus,
} from '@dbl/services/numberPackage';
import {formatNumberByComma, useDblIntl} from '@/pages/datalab/utils/utils';
import { DATALAB_EDITOR, SYSTEM_NAME } from '@dbl/core/global';
import { AuthPermissionDisablesContainers } from '../../../WorkBench/MyDashboard';

// 表格分页
interface PageType {
  pageSize: number;
  pageIndex: number;
}

// 上传参数请求
interface TableRequestParams {
  game_code: string; // 游戏编码
  sub_labels: []; // 类目
  keywords: string | undefined; // 搜索内容
  order_by: string; // 排序字段, key值
  desc: boolean; // true是倒序, false是正序
  page: number; // 分页页码
  page_size: number; // 每页条数
}

// 表格Props
interface UserPackagesTableProps {
  gameCode: string;
  searchWords?: string | undefined;
  requestUserPackagesTable?: number;
  clearSearchWords: any;
}

const { pageable, scrollable, sortable, autotip } = Table.addons;

// 排序列定义
const sortColumns: SortableColumn[] = [
  { key: 'package_size', prefer: 'asc' }, // 包规模
  { key: 'create_time', prefer: 'desc' }, // 创建时间
];


function UserPackagesTable(props: UserPackagesTableProps) {
  const { dblFormatMessage: numFormatMessage, isEn, commonFormatMessage} = useDblIntl("number_package");
  const STATUS_TEXT = {
    1: ['orange', numFormatMessage("pending")],
    2: ['green', numFormatMessage("ready")],
    3: ['normal', numFormatMessage("extracting")],
    4: ['red', numFormatMessage("abnormal")],
  };
  
  const TYPE_TEXT = {
    0: numFormatMessage("static"),
    1: numFormatMessage("dynamic"),
    2: numFormatMessage("static"),
  };
  
  const [records, setRecords] = useState<any[]>([]);

  const [sort, setSort] = useState<SortBy>({ by: 'create_time', order: 'desc' });
  // 分页信息
  const [pageInfo, setPageInfo] = useState<PageType>({ pageSize: 10, pageIndex: 1 });
  // 总条数
  const [total, setTotal] = useState<number>(0);
  // 提取中的任务
  const [tasks, setTasks] = useState<any[]>([]);

  useEffect(() => {}, [JSON.stringify(tasks)]);

  useInterval(
    () => {
      getNumberPackageStatus({
        game_code: props?.gameCode,
        package_uuids: tasks.map((item: any) => String(item.package_uuid)),
      }).then((res: any) => {
        const newTaskRes = res.data_list || [];
        const newTaskIds = newTaskRes
          .filter((item: any) => item.status !== 1 && item.status !== 3)
          .map((item: any) => item.package_uuid);
        const newTask = tasks.filter((item: any) => !newTaskIds.includes(item.package_uuid));
        setTasks(newTask);

        (newTaskRes.filter((item: any) => item.status === 2).map((item: any) => item.package_uuid) || []).forEach(
          (taskId: string) => {
            const record = records.find((item) => item.package_uuid === taskId);
            if (record.type === 2) {
              notification.success({
                description: `上传${record.name}成功`,
              });
            }
          },
        );

        const newRecords = records.reduce((previous: any[], current: any) => {
          const findTask = newTaskRes.find((item: any) => item.package_uuid === current.package_uuid) || current;
          const newCurrent = {
            ...current,
            status: findTask.status,
            package_size: findTask.package_size,
          };
          previous.push(newCurrent);
          return previous;
        }, []);
        setRecords(newRecords);
      });
    },
    tasks.length > 0 ? 10000 : -1,
  );

  const { loading, error, run } = useRequest(
    (pageInfo, sortInfo) => {
      /**
       * 表格请求
       * @param pageInfo 分页信息, 主要用于在切换分页条数, 跳转页码时, 传入最新分页信息
       * @param sortInfo 排序信息, 主要用于在点按排序按钮时, 传入最新排序信息
       */
      setRecords([]); // 表格数据设置为空数组是为了显示tea组件自带的加载提示时, 不显示表格数据
      setPageInfo(pageInfo); // 在这里设置分页信息是因为在 类目和搜索筛选后要重置分页信息, 同时可以兼顾用户切换表格分页后的变化
      setSort(sortInfo);

      // 上传参数
      const params: TableRequestParams = {
        game_code: props?.gameCode,
        keywords: props?.searchWords,
        order_by: sortInfo?.by,
        desc: sortInfo?.order === 'desc',
        page: pageInfo.pageIndex,
        page_size: pageInfo.pageSize,
      };
      return getNumberPackageList(params);
    },
    {
      manual: true,
      onSuccess: (res) => {
        setRecords(res?.data_list || []);
        setTotal(res?.row_count || 0);
        const newTask = [
          ...tasks,
          ...(res?.data_list?.filter(
            (item: any) =>
              (item.status === 1 || item.status === 3) &&
              !tasks.some((task: any) => task.package_uuid === item.package_uuid),
          ) || []),
        ];
        setTasks(newTask);
      },
      onError: (e) => {
        console.log('数据表管理页面 表格请求错误: ', e);
      },
    },
  );

  // 列定义
  const columns: TableColumn[] = [
    {
      key: 'name',
      header: numFormatMessage("name"),
      width: 240,
    },
    {
      key: 'type',
      header: numFormatMessage("type"),
      width: 140,
      // fixed: 'left',
      render(record) {
        return TYPE_TEXT[record.type];
      },
    },
    {
      key: 'column_infos',
      header: numFormatMessage("extracted_fields"),
      width: 154,
      render(record) {
        try {
          const info = JSON.parse(record?.column_infos) || {};
          return info.column || '-';
        } catch (e) {
          console.error('号码包管理页面, 表格提取字段后端返回的不是JSON格式', e);
          return '-';
        }
      },
    },
    {
      key: 'package_size',
      header: numFormatMessage("size"),
      width: 140,
      align: 'right',
      render(record) {
        // 千分隔符转换
        return record.status === 3
          ? numFormatMessage("extracting")
          : formatNumberByComma(`${record?.package_size} ${record.type === 1 ? `(${record.inc_size})` : ''} `);
      },
    },
    {
      key: 'remark',
      header: numFormatMessage("note"),
      width: 240,
      render(record) {
        // 没有备注显示横杆
        return record?.remark ? record.remark : '-';
      },
    },
    {
      key: 'status',
      header: numFormatMessage("status"),
      width: 122,
      render(record) {
        const status: any[] = STATUS_TEXT[record?.status] || [];
        return (
          <div className="state-flex">
            <i className={`icon-${status[0]}`} />
            <Bubble dark content={record.err_msg} placement={'top'}>
              {status[1]}
            </Bubble>
          </div>
        );
      },
    },
    {
      key: 'create_time',
      header: numFormatMessage("creation_time"),
      width: 190,
    },
    {
      key: 'owner',
      header: numFormatMessage("creator"),
      width: 144,
    },
    {
      key: 'handle',
      header: numFormatMessage("operation"),
      width: 162,
      render: (record: any) => (
        <>
          <AuthPermissionDisablesContainers   type={DATALAB_EDITOR.AddChartBtn} customDisabled={record.status !== 2}>
            <Button
              key="1"
              className="table-btn"
              type="link"
              onClick={() => {
                history.push(
                  `/${SYSTEM_NAME}/${props?.gameCode}/Analysis/MultiAnalysis?package_uuid=${record?.package_uuid}`,
                );
              }}
            >
              {numFormatMessage("analyze")}
            </Button>
          </AuthPermissionDisablesContainers>
          <Button
            key="2"
            className="table-btn"
            type="link"
            style={{ display: record.type === 2 ? 'none' : '' }}
            onClick={() => {
              history.push(
                `/${SYSTEM_NAME}/${props?.gameCode}/Analysis/PacketExtraction?package_uuid=${record?.package_uuid}&packageName=${record.name}`,
              );
            }}
          >
            {numFormatMessage("edit")}
          </Button>
        </>
      ),
      fixed: 'right',
    },
  ];
  // 发起表格请求
  useEffect(() => {
    run({ pageSize: 10, pageIndex: 1 }, { by: 'create_time', order: 'desc' });
  }, [props?.gameCode, props?.requestUserPackagesTable, props?.searchWords]);

  return (
    <ConfigProvider locale={isEn ? "en" : "zh"}>
      <Table
        recordKey="package_uuid"
        className="number_package-table"
        columns={columns}
        records={records}
        addons={[
          scrollable({
          // maxHeight: 350,
            minWidth: 1036,
          }),
          pageable({
            isPagingReset: true,
            pageSize: pageInfo?.pageSize,
            pageIndex: pageInfo?.pageIndex,
            pageSizeOptions: [10, 20, 50, 100],
            recordCount: total,
            stateText: numFormatMessage("total", {total}),
            stateTextVisible: total > 10,
            pageSizeVisible: total > 10,
            pageIndexVisible: total > 10,
            jumpVisible: total > 10,
            endJumpVisible: total > 10,
            onPagingChange: (query: PagingQuery) => {
            // 请求表格数据
            // requestDataTable(query, sort);
              run(query, sort);
            },
          }),
          sortable({
            columns: sortColumns,
            value: [sort],
            onChange: (value: SortBy[]) => {
            // 如果value数组没有元素说明当前是未排序状态, 那么上传给后端的参数就应该是按照创建时间降序排序
            //   const sort = value?.length > 0 ? context.sort : { by: 'create_time', order: undefined };
              // requestDataTable(pageInfo, sort);
              run(pageInfo, value[0] ?? {});
            },
          }),
          autotip({
            isLoading: loading,
            isError: !!error,
            isFound: Boolean(props?.searchWords) && total === 0, // 是否显示
            foundText: commonFormatMessage("not_search"),
            onClear: () => {
            // 搜索结果为空时, 显示返回原列表按钮
              props.clearSearchWords();
            },
            style: { lineHeight: '200px' },
          }),
        ]}
      /></ConfigProvider>
  );
}

export default UserPackagesTable;
